import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import ProgressBar from './ProgressBar';
import UploadStatusType, { UploadStatus } from '../../utils/FilesUtils';

interface FileListProps {
  files: File[];
  onFileSelect: (index: number | null) => void;
  onFileAdd: (newFile: File) => void;
  selectedFileIndex: number | null;
  uploadStatus?: UploadStatusType;
  uploadingFileName?: string;
}

const modalInfo = (status: UploadStatus | undefined) => {
  switch (status) {
    case UploadStatus.FAILED:
      return {
        title: 'Failed uploading the file',
        description: 'The file could not be uploaded. Please try again.',
      };
    case UploadStatus.UPLOADING:
      return {
        title: 'Uploading...',
        description:
          'The file is being uploaded. This process may take a few minutes to complete.',
      };
    case UploadStatus.UPLOADED:
      return {
        title: 'Upload completed',
        description: 'The file was successfully uploaded.',
      };
    default:
      return {
        title: 'Uploading...',
        description:
          'The file is being uploaded. This process may take a few minutes to complete.',
      };
  }
};

const FileList: React.FC<FileListProps> = ({
  files,
  onFileSelect,
  onFileAdd,
  selectedFileIndex,
  uploadStatus,
  uploadingFileName,
}) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isModalOpen) {
      updateProgressBar();
    } else {
      setProgress(0);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (uploadStatus) {
      if (uploadStatus.status === UploadStatus.UPLOADING) {
        setIsModalOpen(true);
      } else {
        setProgress(100);
      }
    }
  }, [uploadStatus]);

  const onSelectFile = (index: number) => {
    onFileSelect(index);
  };

  const onModalClose = () => {
    setIsModalOpen(false);
    setProgress(0);
  };

  const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isModalOpen) return;
    setIsDraggingOver(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
    const droppedFile = event.dataTransfer.files?.[0];
    setIsModalOpen(true);
    updateProgressBar();
    if (droppedFile) onFileAdd(droppedFile);
  };

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) onFileAdd(selectedFile);
  };

  const updateProgressBar = () => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
        }
        return prev + 10;
      });
    }, 1000);
  };

  return (
    <div
      className="fileList"
      onDrop={onDrop}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={(event) => event.preventDefault()}
    >
      <input
        type="file"
        style={{ display: 'none' }}
        onChange={onFileInputChange}
      />
      {isDraggingOver ? (
        <div id="drop-zone" className="drop-zone">
          <p>Drag & drop files here to upload</p>
        </div>
      ) : (
        <div className={`fileList-container`}>
          {files.length ? (
            <ul className="fileList-list">
              {files.map((file, index) => (
                <button
                  className={`fileList-list_item ${
                    selectedFileIndex === index ? 'selected' : ''
                  }`}
                  key={index}
                  onClick={() => onSelectFile(index)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      onSelectFile(index);
                    }
                  }}
                >
                  {file.name}
                </button>
              ))}
            </ul>
          ) : (
            <div className="fileList-list_empty">
              <p className="fileList-list_empty-title">
                This bucket is currently empty
              </p>
              <p className="fileList-list_empty-subtitle">
                Drag and drop a file to upload into the bucket
              </p>
            </div>
          )}
          <Modal
            title={modalInfo(uploadStatus?.status).title}
            isOpen={isModalOpen}
            onClose={onModalClose}
          >
            <>
              <p>{uploadingFileName}</p>
              <span>
                <p>
                  {modalInfo(uploadStatus?.status).description ??
                    uploadStatus?.description}
                </p>
              </span>
            </>
            <div className="modal_progressBar">
              <ProgressBar value={progress} color={uploadStatus?.status===UploadStatus.FAILED ? '#DA1E28': ''}/>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default FileList;
